import type { SupabaseClient } from "@supabase/supabase-js";
import { isUnassignedIntegrationFilter } from "@/lib/conversation-integration-assign";

export type FetchInboxPageParams = {
  workspaceId: string;
  channel: "whatsapp" | "instagram" | "messenger";
  integrationId?: string | null;
  isClosed?: boolean | null;
  since?: string | null;
  before?: { lastMessageAt: string | null; id: string } | null;
  ids?: string[] | null;
  limit: number;
};

/** Depois de um "função não existe", não repetir o erro a cada busca. */
let inboxPageRpcDisabled = false;

function isMissingInboxPageRpc(error: { code?: string; message?: string }): boolean {
  return error.code === "PGRST202" || /fetch_inbox_page/i.test(error.message ?? "");
}

/**
 * Primeira página, delta e promoção numa busca que calcula a permissão uma vez.
 * `null` significa falha: o chamador usa a busca antiga.
 */
export async function fetchInboxPageRows(
  client: SupabaseClient,
  params: FetchInboxPageParams,
): Promise<Record<string, unknown>[] | null> {
  if (inboxPageRpcDisabled) return null;

  const filterUnassigned = isUnassignedIntegrationFilter(params.integrationId);
  const integrationId =
    params.integrationId && !filterUnassigned ? params.integrationId : null;

  const { data, error } = await client.rpc("fetch_inbox_page", {
    p_workspace_id: params.workspaceId,
    p_channel: params.channel,
    p_filter_unassigned: filterUnassigned,
    p_limit: params.limit,
    ...(integrationId ? { p_integration_id: integrationId } : {}),
    ...(params.isClosed != null ? { p_is_closed: params.isClosed } : {}),
    ...(params.since ? { p_since: params.since } : {}),
    ...(params.before?.id ? { p_before_id: params.before.id } : {}),
    ...(params.before?.lastMessageAt
      ? { p_before_last_message_at: params.before.lastMessageAt }
      : {}),
    ...(params.before && !params.before.lastMessageAt ? { p_before_null_tail: true } : {}),
    ...(params.ids && params.ids.length > 0 ? { p_ids: params.ids } : {}),
  });

  if (error) {
    if (isMissingInboxPageRpc(error)) inboxPageRpcDisabled = true;
    console.error("fetch_inbox_page:", error);
    return null;
  }

  if (!Array.isArray(data)) return null;
  return data as Record<string, unknown>[];
}
